import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { toPng } from 'html-to-image'
import type { BulletinSettings, BulletinTemplate } from '@fellowship/shared'
import { useChurch, useChurchEvents } from '../hooks/useChurches'
import { useUpdateChurch } from '../hooks/useChurchAdmin'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn, Segmented } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import {
  BULLETIN_TEMPLATES,
  PRINT_TEMPLATE_ORDER,
  SCREEN_TEMPLATE_ORDER,
  DISPLAY_WIDTH,
  DISPLAY_HEIGHT,
} from '../components/bulletin/BulletinTemplates'
import { SlideshowPlayer } from '../components/bulletin/SlideshowPlayer'

const DEFAULT_VERSE = '"Come, let us bow down in worship, let us kneel before the Lord our Maker." — Psalm 95:6'

const DEFAULT_SETTINGS: BulletinSettings = {
  template: 'classic',
  showLogo: true,
  showServiceTimes: true,
  showEvents: true,
  showGiving: true,
  showVerse: true,
  maxEvents: 4,
  verse: DEFAULT_VERSE,
  slideshowAdvance: 'auto',
  slideshowIntervalSeconds: 8,
  slideshowShowImage: true,
  slideshowShowDetails: true,
  slideshowShowQr: true,
}

export default function BulletinPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const { data: eventsData, isLoading: eventsLoading } = useChurchEvents(slug)

  const church = churchData?.data
  const updateChurch = useUpdateChurch(church?.id ?? '')

  const [settings, setSettings] = useState<BulletinSettings | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [fsScale, setFsScale] = useState(1)
  const [downloading, setDownloading] = useState(false)
  const previewNodeRef = useRef<HTMLDivElement>(null)

  // Hydrate settings from the saved church config exactly once per church.
  const hydratedChurchId = useRef<string | null>(null)
  const savedJson = useRef<string>('')
  useEffect(() => {
    if (church && hydratedChurchId.current !== church.id) {
      // Merge over defaults (not just `?? DEFAULT_SETTINGS`) so a church that
      // saved settings before the slideshow fields existed still gets them.
      const initial = { ...DEFAULT_SETTINGS, ...(church.bulletinSettings ?? {}) }
      setSettings(initial)
      savedJson.current = JSON.stringify(initial)
      hydratedChurchId.current = church.id
    }
  }, [church])

  // Debounced autosave so multiple coordinators share one config.
  useEffect(() => {
    if (!settings || !church?.id) return
    const json = JSON.stringify(settings)
    if (json === savedJson.current) return
    setSaveState('saving')
    const t = setTimeout(() => {
      updateChurch.mutate(
        { bulletinSettings: settings },
        {
          onSuccess: () => {
            savedJson.current = json
            setSaveState('saved')
          },
          onError: () => setSaveState('idle'),
        },
      )
    }, 700)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, church?.id])

  const profileUrl = slug ? `${window.location.origin}/churches/${slug}` : ''
  useEffect(() => {
    if (!profileUrl) return
    QRCode.toDataURL(profileUrl, { width: 240, margin: 1, color: { dark: '#1a1a1a', light: '#ffffff' } })
      .then(setQrDataUrl)
      .catch(() => {})
  }, [profileUrl])

  const [eventQrMap, setEventQrMap] = useState<Record<string, string>>({})

  // Full-screen the display template scaled to fill the screen: requestFullscreen()
  // only makes the target element fixed/inset-0, it doesn't resize its fixed-px
  // content — so we scale the node ourselves to fit whatever screen it lands on.
  useEffect(() => {
    const computeScale = () => Math.min(window.innerWidth / DISPLAY_WIDTH, window.innerHeight / DISPLAY_HEIGHT)
    const onFullscreenChange = () => {
      const active = document.fullscreenElement === previewNodeRef.current
      setIsFullscreen(active)
      setFsScale(active ? computeScale() : 1)
    }
    const onResize = () => {
      if (document.fullscreenElement === previewNodeRef.current) setFsScale(computeScale())
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const handleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.()
    } else {
      previewNodeRef.current?.requestFullscreen?.().catch(() => {})
    }
  }

  const handleDownloadImage = async () => {
    if (!previewNodeRef.current || downloading) return
    setDownloading(true)
    try {
      const dataUrl = await toPng(previewNodeRef.current, {
        width: DISPLAY_WIDTH,
        height: DISPLAY_HEIGHT,
        pixelRatio: 2,
        // The church logo is served from S3 without CORS headers, so drawing
        // it taints the export canvas and makes the whole PNG export throw.
        // Drop cross-origin images from the export rather than fail silently —
        // see github.com/Dannzzor/AdventistFellowship/issues/225.
        filter: (node) =>
          !(node instanceof HTMLImageElement && node.src && !node.src.startsWith('data:') && !node.src.startsWith(window.location.origin)),
      })
      const a = document.createElement('a')
      a.href = dataUrl
      a.download = `${slug ?? 'church'}-screen-bulletin.png`
      a.click()
    } catch {
      // Best-effort export — leave the preview visible either way so the
      // church can still fall back to a screenshot or full-screen view.
    } finally {
      setDownloading(false)
    }
  }

  const update = (patch: Partial<BulletinSettings>) => setSettings((s) => (s ? { ...s, ...patch } : s))

  const allOccurrences = (eventsData?.data ?? [])
    .filter((o) => !o.isCancelled)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())

  const isLoading = churchLoading || eventsLoading
  const template: BulletinTemplate = settings?.template ?? 'classic'
  const pageSize = BULLETIN_TEMPLATES[template].pageSize
  const Template = BULLETIN_TEMPLATES[template].Component
  const occurrences = settings ? allOccurrences.slice(0, settings.maxEvents) : []
  const isDisplayTemplate = template === 'display'
  const isSlideshowTemplate = template === 'slideshow'
  const isScreenTemplate = isDisplayTemplate || isSlideshowTemplate

  // Slideshow slides link to the specific event, not the church profile — one
  // QR code per shown occurrence, keyed by occurrence id.
  const occurrenceIdsKey = occurrences.map((o) => o.id).join(',')
  useEffect(() => {
    if (!isSlideshowTemplate || occurrences.length === 0) {
      setEventQrMap({})
      return
    }
    let cancelled = false
    Promise.all(
      occurrences.map((occ) =>
        QRCode.toDataURL(`${window.location.origin}/events/${occ.event.id}`, {
          width: 240,
          margin: 1,
          color: { dark: '#1a1a1a', light: '#ffffff' },
        })
          .then((url): [string, string] => [occ.id, url])
          .catch((): [string, string | null] => [occ.id, null]),
      ),
    ).then((pairs) => {
      if (cancelled) return
      setEventQrMap(Object.fromEntries(pairs.filter((p): p is [string, string] => p[1] !== null)))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSlideshowTemplate, occurrenceIdsKey])

  return (
    <AdminShell>
      <style>{`
        @media print {
          .bulletin-no-print { display: none !important; }
          aside { display: none !important; }
          body { background: white !important; }
          @page { size: ${pageSize}; margin: 0; }
          main { padding: 0 !important; }
          .bulletin-layout { display: block !important; }
          .bulletin-preview { display: block !important; overflow: visible !important; }
          .bulletin-paper {
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
          }
        }
      `}</style>

      <div className="bulletin-no-print">
        <AdminPageHeader
          eyebrow={isScreenTemplate ? 'Screen bulletin' : 'Bulletin insert'}
          title="Bulletin"
          titleItalic="insert."
          sub={
            isSlideshowTemplate
              ? 'Cycles one event at a time on a lobby TV — auto-advancing or tap-to-advance, full-screen only.'
              : isDisplayTemplate
                ? 'A widescreen slide for a lobby TV or slideshow — download it as an image or show it full-screen.'
                : 'A printable insert for your welcome table or announcements.'
          }
          actions={
            <div className="flex items-center gap-3">
              {saveState !== 'idle' && (
                <span className="text-[12px] text-muted">{saveState === 'saving' ? 'Saving…' : 'Saved'}</span>
              )}
              {isSlideshowTemplate ? (
                <AdmBtn variant="primary" icon="play" onClick={handleFullscreen}>
                  {isFullscreen ? 'Exit slideshow' : 'Present slideshow'}
                </AdmBtn>
              ) : isDisplayTemplate ? (
                <>
                  <AdmBtn variant="soft" icon="expand" onClick={handleFullscreen}>
                    {isFullscreen ? 'Exit full screen' : 'View full screen'}
                  </AdmBtn>
                  <AdmBtn variant="primary" icon="download" onClick={handleDownloadImage} disabled={downloading}>
                    {downloading ? 'Preparing…' : 'Download image'}
                  </AdmBtn>
                </>
              ) : (
                <AdmBtn variant="primary" icon="download" onClick={() => window.print()}>
                  Print / Save as PDF
                </AdmBtn>
              )}
            </div>
          }
        />
      </div>

      {isLoading || !settings ? (
        <div className="text-[13px] text-muted mt-4">Loading…</div>
      ) : (
        <div className="bulletin-layout flex gap-6 items-start">
          {/* ── Settings panel ───────────────────────────────────────────── */}
          <div className="bulletin-no-print w-[260px] shrink-0 flex flex-col gap-4">
            <AdminCard title="Design">
              <div className="flex flex-col gap-2">
                {PRINT_TEMPLATE_ORDER.map((key) => {
                  const meta = BULLETIN_TEMPLATES[key]
                  const active = settings.template === key
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => update({ template: key })}
                      className={`text-left rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                        active ? 'border-primary bg-primary/5' : 'border-line-soft bg-surface hover:border-line'
                      }`}
                    >
                      <div className={`text-[13px] font-semibold ${active ? 'text-primary' : 'text-ink-2'}`}>{meta.label}</div>
                      <div className="text-[11.5px] text-muted mt-0.5">{meta.blurb}</div>
                    </button>
                  )
                })}
              </div>

              {/* Screen template(s) — styled like the slide itself (dark, widescreen swatch)
                  so it reads as "made for a TV" at a glance instead of blending in with the
                  white print options above. */}
              <div className="mt-4 pt-4 border-t border-line-soft">
                <div className="flex items-center gap-1.5 mb-2">
                  <AdmIcn name="monitor" size={12} />
                  <span className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-muted">For your screen</span>
                </div>
                <div className="flex flex-col gap-2">
                  {SCREEN_TEMPLATE_ORDER.map((key) => {
                    const meta = BULLETIN_TEMPLATES[key]
                    const active = settings.template === key
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => update({ template: key })}
                        className={`text-left rounded-lg border px-3 py-2.5 cursor-pointer transition-colors text-white ${
                          active ? 'border-primary' : 'border-transparent hover:border-primary/50'
                        }`}
                        style={{ background: 'linear-gradient(160deg, #171B26 0%, #0B0E14 100%)' }}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/10 shrink-0">
                              <AdmIcn name={meta.icon} size={13} />
                            </span>
                            <span className="text-[13px] font-semibold">{meta.label}</span>
                          </div>
                          {meta.badge && (
                            <span className="text-[9px] font-bold tracking-[0.08em] uppercase text-white/55 border border-white/20 rounded-full px-2 py-[3px] whitespace-nowrap">
                              {meta.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-[11.5px] mt-1.5 text-white/65 leading-snug">{meta.blurb}</div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </AdminCard>

            {isSlideshowTemplate ? (
              <AdminCard title="Slideshow">
                <div className="flex flex-col gap-3.5">
                  <div>
                    <div className="text-[12px] font-semibold text-ink-2 mb-1.5">Advance</div>
                    <Segmented
                      value={settings.slideshowAdvance ?? 'auto'}
                      options={[
                        { id: 'auto', label: 'Auto' },
                        { id: 'click', label: 'Click' },
                      ]}
                      onChange={(val) => update({ slideshowAdvance: val as 'auto' | 'click' })}
                    />
                  </div>

                  {(settings.slideshowAdvance ?? 'auto') === 'auto' && (
                    <div>
                      <div className="text-[12px] font-semibold text-ink-2 mb-1.5">Seconds per slide</div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => update({ slideshowIntervalSeconds: Math.max(3, (settings.slideshowIntervalSeconds ?? 8) - 1) })}
                          className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                        >
                          <AdmIcn name="minus" size={13} />
                        </button>
                        <span className="text-[14px] font-semibold w-[24px] text-center">{settings.slideshowIntervalSeconds ?? 8}</span>
                        <button
                          type="button"
                          onClick={() => update({ slideshowIntervalSeconds: Math.min(60, (settings.slideshowIntervalSeconds ?? 8) + 1) })}
                          className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                        >
                          <AdmIcn name="plus" size={13} />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="pt-3 border-t border-line-soft">
                    <div className="text-[12px] font-semibold text-ink-2 mb-2">Show on each slide</div>
                    <div className="flex flex-col gap-2.5">
                      {([
                        { key: 'slideshowShowImage', label: 'Event image' },
                        { key: 'slideshowShowDetails', label: 'Event details' },
                        { key: 'slideshowShowQr', label: 'QR code' },
                      ] as const).map(({ key, label }) => (
                        <label key={key} className="flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings[key] ?? true}
                            onChange={(e) => update({ [key]: e.target.checked } as Partial<BulletinSettings>)}
                            className="w-[15px] h-[15px] rounded cursor-pointer accent-primary"
                          />
                          <span className="text-[13px] text-ink-2">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </AdminCard>
            ) : (
              <AdminCard title="Sections">
                <div className="flex flex-col gap-2.5">
                  {([
                    { key: 'showLogo', label: 'Church logo' },
                    { key: 'showServiceTimes', label: 'Service times' },
                    { key: 'showEvents', label: 'Upcoming events' },
                    { key: 'showGiving', label: 'Giving link' },
                    { key: 'showVerse', label: 'Scripture verse' },
                  ] as const).map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings[key]}
                        onChange={(e) => update({ [key]: e.target.checked } as Partial<BulletinSettings>)}
                        className="w-[15px] h-[15px] rounded cursor-pointer accent-primary"
                      />
                      <span className="text-[13px] text-ink-2">{label}</span>
                    </label>
                  ))}
                </div>
              </AdminCard>
            )}

            {(isSlideshowTemplate || settings.showEvents) && (
              <AdminCard title="Events to show">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => update({ maxEvents: Math.max(1, settings.maxEvents - 1) })}
                    className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                  >
                    <AdmIcn name="minus" size={13} />
                  </button>
                  <span className="text-[14px] font-semibold w-[24px] text-center">{settings.maxEvents}</span>
                  <button
                    type="button"
                    onClick={() => update({ maxEvents: Math.min(8, settings.maxEvents + 1) })}
                    className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                  >
                    <AdmIcn name="plus" size={13} />
                  </button>
                  <span className="text-[12px] text-muted ml-1">of {allOccurrences.length}</span>
                </div>
              </AdminCard>
            )}

            {!isSlideshowTemplate && settings.showVerse && (
              <AdminCard title="Scripture verse">
                <textarea
                  value={settings.verse}
                  onChange={(e) => update({ verse: e.target.value })}
                  rows={4}
                  placeholder="Enter a verse…"
                  className="w-full text-[12.5px] text-ink-2 resize-none border border-line-soft rounded-lg px-3 py-2 bg-bg outline-none focus:border-primary transition-colors leading-[1.5] box-border"
                />
                <button
                  type="button"
                  onClick={() => update({ verse: DEFAULT_VERSE })}
                  className="mt-1.5 text-[11.5px] text-muted bg-transparent border-0 cursor-pointer underline underline-offset-2 p-0"
                >
                  Reset to default
                </button>
              </AdminCard>
            )}

            {!isSlideshowTemplate && (
            <AdminCard title="Service times">
              {(church?.serviceTimes?.length ?? 0) === 0 ? (
                <div className="text-[12px] text-muted">
                  No service times set.{' '}
                  <a href={`/churches/${slug}/edit`} className="text-primary underline underline-offset-2">
                    Add them in Church profile.
                  </a>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {church?.serviceTimes.map((st, i) => (
                    <div key={i} className="text-[12.5px] text-ink-2">
                      <span className="font-semibold">{st.name}</span>
                      {st.day ? ` · ${st.day}` : ''}
                      {st.time ? ` · ${st.time}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </AdminCard>
            )}
          </div>

          {/* ── Live preview ─────────────────────────────────────────────── */}
          <div className="bulletin-preview flex-1 flex justify-center overflow-auto">
            {church && (
              <div
                ref={previewNodeRef}
                style={
                  isFullscreen
                    ? {
                        position: 'fixed',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#000',
                      }
                    : undefined
                }
              >
                <div style={isFullscreen ? { transform: `scale(${fsScale})` } : undefined}>
                  {isSlideshowTemplate ? (
                    <SlideshowPlayer
                      church={church}
                      occurrences={occurrences}
                      eventQrMap={eventQrMap}
                      showImage={settings.slideshowShowImage ?? true}
                      showDetails={settings.slideshowShowDetails ?? true}
                      showQr={settings.slideshowShowQr ?? true}
                      advanceMode={settings.slideshowAdvance ?? 'auto'}
                      intervalSeconds={settings.slideshowIntervalSeconds ?? 8}
                      active={isFullscreen}
                    />
                  ) : (
                    Template && (
                      <Template
                        church={church}
                        occurrences={occurrences}
                        qrDataUrl={qrDataUrl}
                        profileUrl={profileUrl}
                        settings={settings}
                      />
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminShell>
  )
}
